import React, { useState, useEffect, useReducer, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Typography,
  Box,
  Button,
  Input,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import { get } from "aws-amplify/api";
import { I18n } from "aws-amplify/utils";
import currentUserToken from "CurrentUserToken";
import { APIStatusDialog } from "popup/APIStatusDialog";
import { useTemplateController } from "context";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { render } from "@testing-library/react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    return { audit: action.value, fetched: true };
  }
  return lastState;
};
const reducer2 = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    return { audit: action.value, fetched: true };
  }
  return lastState;
};

function EditPermission({
  open,
  onClose,
  userData,
  onSave,
  profileUser,
  setProfileUser,
}) {
  /* -------------------------------------------------------- USESTATE -------------------------------------------*/
  const [hubs, setHubs] = useState([]);
  const [controller] = useTemplateController();
  const { darkMode } = controller;
  const [searchText, setSearchText] = useState("");
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const theme = useTheme();
  const laptopDevice = useMediaQuery("(min-width:601px) and (max-width:1699px");
  const hubRows = Object.entries(hubs)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([hubName, checked], idx) => ({
      id: idx,
      name: hubName,
      value: checked,
    }));

  const columns = useMemo(() => [
    {
      field: "name",
      headerName: I18n.get("Groups"),
      flex: 1,
      renderCell: (params) => {
        return (
          <Typography
            sx={{
              fontWeight: params.row.value ? "bold" : "",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              height: "100%",
            }}
          >
            {params.row.name}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: I18n.get("Actions"),
      width: 80,
      renderCell: (params) => {
        return (
          <Box textAlign={"center"}>
            {params.row.value ? (
              <Tooltip title={I18n.get("Remove permission")}>
                <IconButton
                  aria-label="remove"
                  sx={{ color: "#cc0000" }}
                  onClick={() => {
                    handleChange(params.row.name, params.row.value);
                  }}
                >
                  <RemoveCircleIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={I18n.get("Add permission")}>
                <IconButton
                  aria-label="add"
                  sx={{ color: "#008000" }}
                  onClick={() => {
                    handleChange(params.row.name, params.row.value);
                  }}
                >
                  <AddCircleIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ]);

  /* -------------------------------------------------------- API -------------------------------------------*/
  const hrGetUserGroups = async () => {
    try {
      const restOperation = get({
        apiName: "hrAPI",
        path: "/userManagement/group/list/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: { user_name: userData.user_name },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      setHubs(response[0]);
    } catch (error) {
      console.error("Error fetching user groups: s", error);
    }
  };

  useEffect(() => {
    hrGetUserGroups();
  }, [userData.user_name]);

  const hrPostGroupAssignment = async (groupName, remove) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Updating the user permissions..."),
      });

      const restOperation = get({
        apiName: "hrAPI",
        path: "/userManagement/group/assignment",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            group_name: groupName,
            user_name: userData.user_name,
            remove: remove,
          },
        },
      });
      const response = await restOperation.response;

      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("User permissions successfully updated"),
        });
        hrGetUserGroups();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to update user permissions"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to update user permissions"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };

  /* -------------------------------------------------------- HANDLERS -------------------------------------------*/
  const handleClose = () => {
    onClose(); // Trigger the original onClose callback
  };

  const handleChange = (hubName, value) => {
    hrPostGroupAssignment(hubName, value);
  };

  /* -------------------------------------------------------- FILTERS -------------------------------------------*/
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };

  const filteredRows = hubRows.filter((value) => {
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      return Object.values(value).some((val) =>
        String(val).toLowerCase().includes(searchTextLower)
      );
    }
    return true;
  });

  /* -------------------------------------------------------- JSX -------------------------------------------*/
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          height: laptopDevice ? "75%" : "80%",
          width: "70%",
          borderRadius: "1rem",
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "24px", pb: 0 }}>
        {I18n.get("Edit Permissions")}
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 0 }}>
        <Box
          alignContent={"right"}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Input
            type="text"
            placeholder={I18n.get("Search")}
            onChange={searchChanged}
            sx={{
              "& .MuiInputBase-input": {
                padding: "12px 0px  12px  12px  ",
              },
              "& .MuiFormControl-root-MuiTextField-root": {
                marginTop: "0px",
              },
              maxWidth: "200px",
              mb: 3,
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Box>
        <DataGrid
          columns={columns}
          rows={filteredRows}
          sx={{
            minHeight: "10%",
            maxHeight: laptopDevice ? "75%" : "88%",
          }}
          disableMultipleRowSelection
          hideFooter
        />
      </DialogContent>
      <DialogActions sx={{ padding: "0rem 1.5rem 1rem 1rem" }}>
        <Button onClick={onClose} color="secondary">
          {I18n.get("Close")}
        </Button>
      </DialogActions>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </Dialog>
  );
}

export default EditPermission;
